import React, { useState, useEffect, useCallback } from 'react';
import { useRequest } from 'umi';
import styles from '../css/detailCSS.less';
import { Modal, message, Button, Tooltip } from 'antd';
import { RewardVo } from '@/types/camp/details/detailsType';
import { StudyInfoType } from '@/types/camp/study/studentBaseInfoType';
import { getRewardList, postReceiveReward } from '@/utils/requests/camp/requests_reward';
import { getCampNodes } from '@/services/ConsoleRequest';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import kelingSvg from '@/static/images/r_ke.svg';
import yilingSvg from '@/static/images/r_yi.svg';
import dayjs from 'dayjs';
import Editor from '@digit-fe/digit-editor-new';
import { editParseJson } from '@/utils/index';
import { RewardConditionToText } from '@/pages/camp/util/campState';

interface Props {
  studentBaseInfo: StudyInfoType | null;
}
const CampReward: React.FC<Props> = ({ studentBaseInfo }) => {
  const [dataList, setDataList] = React.useState<RewardVo[]>([]);
  const [itemData, setItemData] = React.useState<RewardVo>();
  const [total, setTotal] = React.useState<number>(0);
  const [nodeList, setNodeList] = React.useState<any>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadings, setLoadings] = useState<boolean[]>([]);
  const [resReward, setResReward] = useState(0);

  // 领取奖励接口
  const { run: postReceiveRewardRequest } = useRequest(
    (rewardId, batchId) => {
      let params = {
        rewardId: rewardId,
        batchId: batchId,
      };
      return postReceiveReward(params);
    },
    {
      manual: true,
      onSuccess: (res) => {
        message.success('领取成功');
        setItemData(res);
        setLoadings([false]);
        setResReward(Math.random());
      },
    },
  );
  // 获取奖励接口
  const { run: rewardListRequest } = useRequest(
    (studentBaseInfo: StudyInfoType) => {
      return getRewardList(studentBaseInfo?.camp.id, studentBaseInfo.batchId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setDataList(res);
        setTotal(res.length);
      },
    },
  );
  // 获取节点接口
  const { run: resNodesRequest } = useRequest(
    (campId) => {
      return getCampNodes(campId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setNodeList(res);
      },
    },
  );

  useEffect(() => {
    resReward && studentBaseInfo?.camp.id && rewardListRequest(studentBaseInfo);
  }, [resReward]);

  useEffect(() => {
    if (studentBaseInfo?.camp.id) {
      resNodesRequest(studentBaseInfo?.camp.id);
      rewardListRequest(studentBaseInfo);
    }
  }, [studentBaseInfo?.id]);

  const rewardConditionText = useCallback(
    (item: any) => {
      let icon = item.state ? (
        <CheckCircleOutlined style={{ color: '#52C41A' }} />
      ) : (
        <CloseCircleOutlined style={{ color: '#FF3D23' }} />
      );

      switch (item.type) {
        case 0:
          return (
            <div className="f14 flex_1">
              {icon}
              <span style={{ paddingLeft: 6 }}>打卡成功</span>
            </div>
          );
        case 1:
          return (
            <div className="f14 flex_1">
              {icon}
              <span style={{ paddingLeft: 6 }}>学分 ≥ {itemData?.condition.earnCredits}</span>
            </div>
          );
        case 2:
          let nodesText = '';
          if (Array.isArray(nodeList)) {
            if (item.nodes.length === nodeList.length) {
              nodesText = '完成所有打卡节点';
            } else {
              nodesText = `完成：${item.nodes.join('，')}`;
            }
          }
          return (
            <div className="f14 flex_1" style={{ alignItems: 'flex-start' }}>
              <span>{icon}</span>
              <span style={{ paddingLeft: 6 }}>{nodesText}</span>
            </div>
          );
        case 3:
          return (
            <div className="f14 flex_1">
              {icon}
              <span style={{ paddingLeft: 6 }}>
                结营后排名：
                {`[排名${itemData?.condition.minRankAfterFinishCamp}~${itemData?.condition.maxRankAfterFinishCamp}名]`}
              </span>
            </div>
          );
        case 4:
          return (
            <div className="f14 flex_1">
              {icon}
              <span style={{ paddingLeft: 6 }}>
                {`${dayjs(studentBaseInfo?.batch?.rewardTime).format(
                  'YYYY-MM-DD HH:mm:ss',
                )} 后开放领取`}
              </span>
            </div>
          );

        default:
          break;
      }
    },
    [itemData, nodeList],
  );

  useEffect(() => {
    let timerId = setTimeout(function () {
      const scrollable = document.getElementById('rewardScroll');
      if (scrollable && total > 3) {
        scrollable.addEventListener('wheel', (event) => {
          event.preventDefault();
          if (event.deltaY < 0) {
            scrollable.scrollLeft += event.deltaY;
          } else {
            scrollable.scrollLeft += event.deltaY;
          }
        });
      }
    }, 1000);
    return () => {
      clearTimeout(timerId);
    };
  }, [total]);

  return (
    <>
      {total > 0 && (
        <div className="com_cmap_card_box" style={{ marginTop: 24, overflow: 'hidden' }}>
          <div className="flex_1 flex_3">
            <div className="f20 fw600">打卡奖励</div>
          </div>
          <div id="rewardScroll" className={styles.camp_reward_box}>
            {dataList.map((item, index) => (
              <div
                key={index}
                className={styles.reward_item}
                onClick={() => {
                  setIsModalOpen(true);
                  setItemData(item);
                }}
              >
                {item.canGetReward && (
                  <div className={styles.r_state}>
                    <img src={kelingSvg} width={40} height={40} />
                  </div>
                )}
                {item.countRewardLog && (
                  <div className={styles.r_state}>
                    <img src={yilingSvg} width={40} height={40} />
                  </div>
                )}
                <div className="flex_4_1" style={{ gap: 8 }}>
                  <div>
                    <img src={item.picture} height={60} width={60} />
                  </div>
                  <Tooltip placement="top" title={item.name}>
                    <div className="f16 hide_txt">{item.name}</div>
                  </Tooltip>
                  <div className="f14 col808 hide_txt2">
                    {nodeList && (
                      <RewardConditionToText nodeList={nodeList} condition={item.condition} />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        open={isModalOpen}
        centered
        width={480}
        onCancel={() => {
          setIsModalOpen(false);
        }}
        footer={null}
      >
        <div className={styles.reward_modal_box}>
          <div className="flex_4_1">
            <div className={styles.reward_modal_jiaobiao}>
              {itemData?.countRewardLog ? (
                <div className={styles.r_state}>
                  <img src={yilingSvg} width={50} height={50} />
                </div>
              ) : itemData?.canGetReward ? (
                <div className={styles.r_state}>
                  <img src={kelingSvg} width={50} height={50} />
                </div>
              ) : null}
            </div>

            <div>
              <img src={itemData?.picture} height={80} width={80} />
            </div>

            <div className="f16 hide_txt com_text_c fw600" style={{ paddingTop: 6 }}>
              {itemData?.name}
            </div>
          </div>
          <div className={styles.reward_modal_info}>
            <div className={styles.info_top}>
              <div>领取条件：</div>
              {itemData?.rewardConditions &&
                itemData?.rewardConditions.map((item, index) => (
                  <div key={index}>{rewardConditionText(item)}</div>
                ))}
            </div>
            {itemData?.description && (
              <div className={styles.info_bottom}>
                <div style={{ marginBottom: 8 }}>
                  {' '}
                  {itemData?.content === 2 ? '奖励说明：' : '使用说明：'}
                </div>
                <div className="col808">{itemData?.description}</div>
              </div>
            )}
          </div>

          <div>
            {itemData?.countRewardLog ? (
              itemData?.content !== 2 ? (
                <a
                  className="com_camp_btn f16"
                  style={{ width: '100%', height: 40, margin: '24px 0 10px 0' }}
                  href={
                    itemData?.content === 0
                      ? `${process.env.ID_URL}#/wallet/`
                      : itemData.contentInfo.netAddr
                  }
                  target="_blank"
                >
                  {itemData?.content === 0 ? '去我的钱包查看' : '去使用'}
                </a>
              ) : (
                <div
                  className={styles.reward_modal_info}
                  style={{ padding: 16, margin: '12px 0 10px' }}
                >
                  <div>
                    <div>领取方式：</div>
                    <div className="com_edit_new" style={{ marginTop: 8 }}>
                      <Editor
                        style={{ backgroundColor: '#F7FAFD' }}
                        content={editParseJson(itemData?.contentInfo?.customTips)}
                        editable={false}
                        titleProps={{ showTitle: false }}
                        catalogProps={{ showCatalog: false }}
                      />
                    </div>
                  </div>
                </div>
              )
            ) : itemData?.canGetReward ? (
              <Button
                style={{
                  width: '100%',
                  height: 40,
                  margin: '24px 0 10px 0',
                }}
                type="primary"
                loading={loadings[0]}
                onClick={() => {
                  setLoadings([true]);
                  postReceiveRewardRequest(itemData.id, studentBaseInfo?.batchId);
                }}
              >
                立即领取
              </Button>
            ) : (
              <Button
                style={{ width: '100%', height: 40, margin: '24px 0 10px 0' }}
                type="primary"
                disabled
              >
                未满足领取条件
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};
export default CampReward;
